import { useState, useEffect, useCallback, useRef } from "react";
import useLocalStorage from "use-local-storage-state";

/**
 * Storage format for drafts - keyed by contextId, then conversationId
 */
interface DraftStorage {
  [contextId: string]: {
    [conversationId: string]: string;
  };
}

const LOCAL_STORAGE_KEY = "sc-agent-drafts";

/**
 * Maximum number of drafts to keep per context to prevent unbounded growth.
 * Older drafts will be removed when this limit is exceeded.
 */
const MAX_DRAFTS_PER_CONTEXT = 50;

export interface UseDraftsOptions {
  /**
   * The context ID (e.g., dataset ID)
   */
  contextId: string;

  /**
   * The current conversation ID
   */
  conversationId: string | null;

  /**
   * Debounce delay for saving drafts (ms)
   * @default 300
   */
  debounceMs?: number;
}

export interface UseDraftsResult {
  /**
   * Current draft value for the active conversation
   */
  draft: string;

  /**
   * Set the draft value for the active conversation
   */
  setDraft: (value: string) => void;

  /**
   * Clear the draft for the active conversation
   */
  clearDraft: () => void;
}

/**
 * Hook for managing per-conversation message drafts with localStorage persistence.
 *
 * Features:
 * - Stores drafts per conversation, allowing users to switch without losing work
 * - Persists across page reloads
 * - Debounces writes to localStorage for performance
 * - Cleans up old drafts to prevent unbounded storage growth
 *
 * Usage:
 * ```tsx
 * const { draft, setDraft, clearDraft } = useDrafts({
 *   contextId: datasetId,
 *   conversationId: currentConversationId,
 * });
 * ```
 */
export function useDrafts({
  contextId,
  conversationId,
  debounceMs = 300,
}: UseDraftsOptions): UseDraftsResult {
  const [storage, setStorage] = useLocalStorage<DraftStorage>(LOCAL_STORAGE_KEY, {
    defaultValue: {},
  });

  // Local state for immediate UI updates (before debounced save)
  const [localDraft, setLocalDraft] = useState<string>("");

  // Refs to track values without triggering effect re-runs
  const prevConversationIdRef = useRef<string | null>(null);
  const localDraftRef = useRef<string>("");
  const contextIdRef = useRef<string>(contextId);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep refs in sync with current values
  localDraftRef.current = localDraft;
  contextIdRef.current = contextId;

  /**
   * Safely update storage with error handling
   */
  const safeSetStorage = useCallback(
    (updater: (prev: DraftStorage) => DraftStorage) => {
      try {
        setStorage(updater);
      } catch (error) {
        // localStorage can fail due to quota exceeded, private browsing, etc.
        console.error("Failed to save draft to localStorage:", error);
      }
    },
    [setStorage]
  );

  /**
   * Load draft from storage when conversation changes.
   * Uses refs to avoid race conditions from stale closure values.
   */
  useEffect(() => {
    if (conversationId === prevConversationIdRef.current) {
      return;
    }

    // Save any pending draft for the previous conversation before switching
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;

      // Immediately save the current draft for the previous conversation using refs
      const prevConvId = prevConversationIdRef.current;
      const currentDraft = localDraftRef.current;
      const currentContextId = contextIdRef.current;

      if (prevConvId && currentDraft) {
        safeSetStorage((prev) => {
          const contextDrafts = prev[currentContextId] || {};
          return {
            ...prev,
            [currentContextId]: {
              ...contextDrafts,
              [prevConvId]: currentDraft,
            },
          };
        });
      }
    }

    // Update ref to current conversation
    prevConversationIdRef.current = conversationId;

    // Load draft for new conversation
    if (conversationId && contextId) {
      try {
        const savedDraft = storage[contextId]?.[conversationId] || "";
        setLocalDraft(savedDraft);
      } catch (error) {
        console.error("Failed to read draft from localStorage:", error);
        setLocalDraft("");
      }
    } else {
      setLocalDraft("");
    }
    // Only re-run when conversationId or contextId changes, not on storage/localDraft changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, contextId]);

  /**
   * Set draft with debounced persistence
   */
  const setDraft = useCallback(
    (value: string) => {
      // Update local state immediately for responsive UI
      setLocalDraft(value);

      // Debounce the storage write
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (!conversationId || !contextId) {
        return;
      }

      debounceTimerRef.current = setTimeout(() => {
        safeSetStorage((prev) => {
          const contextDrafts = prev[contextId] || {};

          // Clean up old drafts if we exceed the limit
          const draftKeys = Object.keys(contextDrafts);
          let updatedContextDrafts = { ...contextDrafts };

          if (draftKeys.length >= MAX_DRAFTS_PER_CONTEXT) {
            // Remove empty drafts first
            const nonEmptyDrafts = Object.entries(updatedContextDrafts).filter(
              ([, v]) => v.trim() !== ""
            );
            updatedContextDrafts = Object.fromEntries(nonEmptyDrafts);

            // If still over limit, remove oldest entries (first in object)
            const remaining = Object.entries(updatedContextDrafts);
            if (remaining.length >= MAX_DRAFTS_PER_CONTEXT) {
              const toKeep = remaining.slice(-MAX_DRAFTS_PER_CONTEXT + 1);
              updatedContextDrafts = Object.fromEntries(toKeep);
            }
          }

          // Set the new draft value (or remove if empty)
          if (value.trim() === "") {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [conversationId]: _, ...rest } = updatedContextDrafts;
            return {
              ...prev,
              [contextId]: rest,
            };
          }

          return {
            ...prev,
            [contextId]: {
              ...updatedContextDrafts,
              [conversationId]: value,
            },
          };
        });
        debounceTimerRef.current = null;
      }, debounceMs);
    },
    [conversationId, contextId, debounceMs, safeSetStorage]
  );

  /**
   * Clear draft for current conversation
   */
  const clearDraft = useCallback(() => {
    setLocalDraft("");

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    if (!conversationId || !contextId) {
      return;
    }

    safeSetStorage((prev) => {
      const contextDrafts = prev[contextId] || {};
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [conversationId]: _, ...rest } = contextDrafts;
      return {
        ...prev,
        [contextId]: rest,
      };
    });
  }, [conversationId, contextId, safeSetStorage]);

  // Flush any pending debounced draft on unmount so it isn't lost
  // (e.g., when Composer remounts due to a conversation-switch key change).
  const safeSetStorageRef = useRef(safeSetStorage);
  safeSetStorageRef.current = safeSetStorage;

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;

        const convId = prevConversationIdRef.current;
        const draft = localDraftRef.current;
        const ctxId = contextIdRef.current;

        if (convId && draft.trim()) {
          safeSetStorageRef.current((prev) => ({
            ...prev,
            [ctxId]: {
              ...(prev[ctxId] || {}),
              [convId]: draft,
            },
          }));
        }
      }
    };
  }, []);

  return {
    draft: localDraft,
    setDraft,
    clearDraft,
  };
}
